import flatpickr from "flatpickr";
import { German } from "flatpickr/dist/l10n/de.js";
import 'select2';

const t = (key, fallback = '') => (window.BL_I18N && BL_I18N[key]) || fallback || key;
const CURRENT_LOCALE = (window.BL_I18N && BL_I18N.locale) || document.documentElement.lang || 'en-US';

const IS_DE = String(CURRENT_LOCALE).toLowerCase().startsWith('de');

class OverviewApartmentApi {

    static ALLOWED_KEYS = Object.freeze([
        'area','bedrooms','guests','stayType','features','dateFrom','dateTo','availability', 'unitType', 'unsureCheckout'
    ]);

    constructor() {
        this.proxyUrl = '/wp-content/themes/basellodging-theme/api-proxi.php';

        this.lang = (
            (window.ICL_LANGUAGE_CODE) ||
            (window.BL_AJAX && BL_AJAX.lang) ||
            (window.BL_I18N && BL_I18N.locale) ||
            document.documentElement.lang || 'en'
        ).toString().toLowerCase().split(/[-_]/)[0];

        // Mounts
        this.sidebarMountSelector  = '#sidebar-filters-mount';
        this.resultsMountSelector  = '#apartments-results-mount';

        // Paging
        this.pageSize = 12;
        this.page = 1;
        this.total = 0;
        this.loading = false;

        // State
        this.guests = 2;
        this.longTermThreshold = 30;

        // View mode (grid | list)
        this.viewStorageKey = 'bl_apts_view';
        this.view = 'grid';

        this.localIndex = {};   // externalId -> { postId, url, title }
        this.localIds = [];     // ["74","112",...]
        this.idsCSV = '';

        this.$mapOverlay = null;
        this.$mapModal   = null;
        this.$mapTitle   = null;
        this.$mapCanvas  = null;
        this.mapInited   = false;

        // map states
        this.map = null;
        this.geocoder = null;
        this.mapMarkers = [];
        this.mapBounds = null;

        // geocode cache (to avoid hitting the quota)
        this.geoCacheKey = 'bl_map_geocode_v1';
        this.geoCache = {};
        try { this.geoCache = JSON.parse(sessionStorage.getItem(this.geoCacheKey) || '{}'); } catch(_) {}

        // mapping "location name -> value in <select>"
        this.areaLabelToValue = {};

        this.$addrInput   = null;
        this.addrAC       = null;     // Autocomplete
        this.searchPlace  = null;     // {lat,lng,name}
        this.searchMarker = null;     // google.maps.Marker

        this.unitType = null;

        // Guests mapping: UI display -> API cap
        this.guestsMap = {};      // { "1": 2, "2": 2, "3": 4, ... }
        this.guestsMaxShown = 1;  // last UI number (max in select)

        this.extraFeatureIds = [];

        this.FORWARD_KEYS = ['dateFrom', 'dateTo', 'guests', 'unsureCheckout'];
    }

    normalizeAreaLabel(txt) {
        return String(txt || '').replace(/\s*\(\d+\)\s*$/, '').trim().toLowerCase();
    }
    saveGeoCache() {
        try { sessionStorage.setItem(this.geoCacheKey, JSON.stringify(this.geoCache)); } catch(_) {}
    }

    _prioritizeFeatures(list = []) {
        const arr = Array.isArray(list) ? [...list] : [];
        const isUSP = (name) => /^\s*USP\b/i.test(String(name || ''));
        return arr.sort((a, b) => {
            const au = isUSP(a.name), bu = isUSP(b.name);
            if (au !== bu) return au ? -1 : 1;
            return String(a.name || '').localeCompare(String(b.name || ''));
        });
    }

    buildGuestsMap(filtersGuests) {
        // filtersGuests example: [{value: 2}, {value: 4}, {value: 6}]
        let caps = Array.from(new Set(
            (filtersGuests || [])
                .map(g => Number(g.value))
                .filter(Number.isFinite)
                .filter(n => n > 0)
        )).sort((a,b) => a - b);

        if (!caps.length) caps = [1];

        const maxShown = caps[caps.length - 1];

        const pickCap = (guestsCount) => {
            for (let i = 0; i < caps.length; i++) {
                if (caps[i] >= guestsCount) return caps[i];
            }
            return caps[caps.length - 1];
        };

        const map = {};
        for (let g = 1; g <= maxShown; g++) {
            map[String(g)] = pickCap(g);
        }

        this.guestsMap = map;
        this.guestsMaxShown = maxShown;
    }

    addForwardParams(baseUrl) {
        // Guard: nothing to do
        if (!baseUrl || baseUrl === '#') return baseUrl;

        // Collect allowed params from current location
        const current = new URLSearchParams(window.location.search);
        const forward = new URLSearchParams();
        this.FORWARD_KEYS.forEach(k => {
            const v = current.get(k) ?? (k === 'guests' ? current.get('maxGuests') : null);
            if (v !== null && v !== '') forward.set(k, v);
        });

        if (current.get('stayType') === 'DAILY') {
            forward.set('stayType', 'DAILY');
        }

        if ([...forward.keys()].length === 0) return baseUrl;

        try {
            // Use URL() to handle relative/absolute + hash safely
            const url = new URL(baseUrl, window.location.origin);

            // Merge without overwriting existing query params in baseUrl
            const existing = new URLSearchParams(url.search);
            forward.forEach((val, key) => {
                if (!existing.has(key)) existing.set(key, val);
            });

            url.search = existing.toString();
            return url.toString();
        } catch (e) {
            // Fallback for odd relative strings
            const qs = forward.toString();
            if (!qs) return baseUrl;
            const hasQuery = baseUrl.includes('?');
            const [path, hash = ''] = baseUrl.split('#');
            const sep = hasQuery ? '&' : '?';
            const rebuilt = path + sep + qs + (hash ? '#' + hash : '');
            return rebuilt;
        }
    }

    async ensureGoogleReady() {
        if (window.google && google.maps) return;
        await new Promise((res, rej) => {
            let tries = 0, t = setInterval(() => {
                if (window.google && google.maps) { clearInterval(t); res(); }
                else if (++tries > 120) { clearInterval(t); rej(new Error('Google Maps not loaded')); }
            }, 50);
        });
    }

    getMapZoom(kind = 'list') {
        // kind: 'list' | 'single'
        const cfg = (window.BL_MAP || {});
        const raw = (kind === 'single') ? cfg.singleZoom : cfg.zoom;
        const n = Number(raw);
        // valid zoom for Google is roughly 3..21 (lower bound not critical)
        return Number.isFinite(n) ? n : (kind === 'single' ? 15 : 13);
    }

    buildGuestsSelectOptions(filtersGuests) {
        // Ensure map is built
        this.buildGuestsMap(filtersGuests);

        const opts = [`<option value="">${t('select_guests','Select guests')}</option>`];

        for (let g = 1; g <= this.guestsMaxShown; g++) {
            opts.push(`<option value="${g}">${g}</option>`);
        }

        return opts.join('');
    }

    async initAddressAutocomplete() {
        this.$addrInput = $('[data-role="map-address"]');
        if (!this.$addrInput.length) return;

        await this.ensureGoogleReady();

        // Basel bounds (approx)
        const baselBounds = new google.maps.LatLngBounds(
            {lat: 47.50, lng: 7.50}, // SW
            {lat: 47.62, lng: 7.72}  // NE
        );

        this.addrAC = new google.maps.places.Autocomplete(this.$addrInput[0], {
            fields: ['geometry','name','formatted_address'],
            types: ['geocode'],
            componentRestrictions: { country: 'ch' },
            bounds: baselBounds,
            strictBounds: true
        });

        // show suggestions only after 3+ characters
        this.$addrInput.on('input', () => {
            const v = (this.$addrInput.val() || '').trim();
            const $pac = $('.pac-container'); // Google's container
            if (v.length < 3) { $pac.hide(); this.searchPlace = null; }
            else { $pac.show(); }
        });

        this.addrAC.addListener('place_changed', () => {
            const place = this.addrAC.getPlace();
            if (place && place.geometry && place.geometry.location) {
                const loc = place.geometry.location;
                this.searchPlace = {
                    lat: loc.lat(),
                    lng: loc.lng(),
                    name: place.name || place.formatted_address || this.$addrInput.val()
                };
            }
        });
    }

    async focusMapToSearch() {
        await this.ensureGoogleReady();
        const map = this.map;
        if (!map) return false;

        // if the user didn't choose from the list — try to geocode the entered text
        if (!this.searchPlace && this.$addrInput && this.$addrInput.val()) {
            const q = String(this.$addrInput.val()).trim();
            if (q.length >= 3) {
                const geocoder = new google.maps.Geocoder();
                await new Promise((res) => {
                    geocoder.geocode({ address: `${q}, Basel, Switzerland` }, (r, s) => {
                        if (s === 'OK' && r && r[0]) {
                            const L = r[0].geometry.location;
                            this.searchPlace = { lat: L.lat(), lng: L.lng(), name: q };
                        }
                        res();
                    });
                });
            }
        }

        if (!this.searchPlace) return false;

        // --- SAFE zoom value (no NaN) ---
        const rawZoom =
            (window.BL_MAP && (window.BL_MAP.singleZoom ?? window.BL_MAP.zoom)) ?? 15;
        const Z = Number.isFinite(Number(rawZoom)) ? Number(rawZoom) : 15;

        // center and zoom to the found place
        try { map.setZoom(Z); } catch (_) { map.setZoom(15); }
        map.panTo(this.searchPlace);

        // custom marker (remove the previous one if any)
        if (this.searchMarker) {
            this.searchMarker.setMap(null);
            this.searchMarker = null;
        }
        this.searchMarker = new google.maps.Marker({
            map,
            position: this.searchPlace,
            title: this.searchPlace.name || t('selected_location','Selected location'),
            zIndex: 1000,
            icon: {
                url:
                    'data:image/svg+xml;charset=UTF-8,' +
                    encodeURIComponent(
                        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#0f172a" stroke="white" stroke-width="1.5">
             <path d="M12 22s8-7.333 8-12A8 8 0 1 0 4 10c0 4.667 8 12 8 12z"/>
             <circle cx="12" cy="10" r="3" fill="white"/>
           </svg>`
                    ),
                scaledSize: new google.maps.Size(32, 32),
                anchor: new google.maps.Point(16, 28),
            },
        });

        return true;
    }

    /* ------------------------ UTILITIES ------------------------ */
    esc(str) {
        return String(str)
            .replace(/&/g,'&amp;').replace(/</g,'&lt;')
            .replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');
    }
    buildOptions(opts, getValue, getLabel, withCounts = false) {
        return (opts || []).map(o => {
            const val = this.esc(getValue(o));
            const lbl = this.esc(getLabel(o));
            const cnt = withCounts && (o.unit_count ?? null);
            return `<option value="${val}">${lbl}${cnt ? ` (${cnt})` : ''}</option>`;
        }).join('');
    }
    addDays(date, days) { const d = new Date(date); d.setDate(d.getDate() + days); return d; }
    parseDateYMD(value) {
        const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!m) return null;
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        d.setHours(0, 0, 0, 0);
        return d;
    }
    formatDateYMD(date) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    getUnsureCheckoutDays() {
        const raw = window.BL_UNSURE_CHECKOUT?.numberOfDaysForDepartureDate;
        const days = parseInt(raw, 10);
        return Number.isFinite(days) && days > 0 ? days : 60;
    }

    isUnsureCheckoutChecked($scope) {
        return $scope.find('[data-role="unsure-checkout"]').is(':checked');
    }

    toggleUnsureCheckoutUI($scope) {
        const $checkout = $scope.find('[data-role="checkout"]');
        const $unsureCheckout = $scope.find('[data-role="unsure-checkout"]');

        const checked = $unsureCheckout.is(':checked');

        if (checked) {
            const el = $checkout[0];
            const fp = el && el._flatpickr;

            this._suppressCheckoutChange = true;

            if (fp) {
                fp.clear();
                fp.close();
            }

            $checkout
                .val('')
                .prop('disabled', true)
                .attr('disabled', 'disabled')
                .addClass('opacity-60 cursor-not-allowed');

            if (fp && fp.input) {
                $(fp.input)
                    .val('')
                    .prop('disabled', true)
                    .attr('disabled', 'disabled')
                    .addClass('opacity-60 cursor-not-allowed');
            }

            setTimeout(() => {
                this._suppressCheckoutChange = false;
            }, 0);
        } else {
            const el = $checkout[0];
            const fp = el && el._flatpickr;

            $checkout
                .prop('disabled', false)
                .removeAttr('disabled')
                .removeClass('opacity-60 cursor-not-allowed');

            if (fp && fp.input) {
                $(fp.input)
                    .prop('disabled', false)
                    .removeAttr('disabled')
                    .removeClass('opacity-60 cursor-not-allowed');
            }
        }
    }

    nf(num) { return new Intl.NumberFormat('en-CH', { maximumFractionDigits: 2 }).format(num); }
    priceLabel(pricing) {
        if (!pricing) return '';
        const amount = (pricing.amount ?? pricing.pricePerNight ?? 0);
        const unit = pricing.rateType === 'MONTHLY' ? `/ ${t('month','month')}` : `/ ${t('night','night')}`;
        return `CHF ${this.nf(amount)} <span class="text-xs text-gray-500">${unit}</span>`;
    }

    getLocalIndex() {
        return new Promise((resolve) => {
            $.ajax({
                url: `${this.proxyUrl}?type=getLocalIndex`,
                method: 'POST',
                dataType: 'json',
                contentType: 'application/json',
                data: JSON.stringify({ lang: this.lang }),
                success: (resp) => {
                    const byExternal = resp?.data?.byExternal || {};
                    const order = Array.isArray(resp?.data?.order) ? resp.data.order : null;

                    this.localIndex = byExternal;

                    this.localIds = order ? order.map(String) : Object.keys(byExternal);

                    this.idsCSV = this.localIds.join(',');
                    resolve(byExternal);
                },
                error: () => { this.localIndex = {}; this.localIds = []; this.idsCSV = ''; resolve({}); }
            });
        });
    }

    ensureMapModal() {
        if (this.$mapModal) return;

        const html = `
  <div id="bl-map-overlay" data-role="map-overlay"
       class="fixed inset-0 z-50 bg-black/80 hidden
              data-[state=open]:animate-in data-[state=closed]:animate-out
              data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"></div>

  <div id="bl-map-modal" data-role="map-modal" role="dialog" aria-modal="true"
       class="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]
              gap-4 border bg-background shadow-lg duration-200 hidden
              data-[state=open]:animate-in data-[state=closed]:animate-out
              data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
              data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
              data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]
              data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]
              sm:rounded-lg max-w-6xl max-h-[90vh] h-[85vh] w-[95vw] sm:w-full p-0 flex flex-col">
    <div class="flex flex-col space-y-1.5 text-center sm:text-left p-6 pb-4 flex-shrink-0">
      <h2 class="text-lg font-semibold leading-none tracking-tight flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin w-5 h-5 mr-2"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
        <span>${t('apartments_in_basel','Apartments in Basel')}</span>
      </h2>
      <p class="text-sm text-muted-foreground">${t('map_subtitle','View apartment locations and details on the map')}</p>
    </div>
    <div class="flex-1 mx-6 mb-6">
      <div data-role="map-canvas" class="w-full h-full bg-slate-100 rounded-xl overflow-hidden shadow-inner"></div>
    </div>
    <button type="button" data-role="map-close"
            class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity
                   hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
      <span class="sr-only">${t('close','Close')}</span>
    </button>
  </div>`;

        $('body').append(html);

        this.$mapOverlay = $('[data-role="map-overlay"]');
        this.$mapModal   = $('[data-role="map-modal"]');
        this.$mapTitle   = $('[data-role="map-title"]');
        this.$mapCanvas  = $('[data-role="map-canvas"]');

        const close = () => this.closeMap();
        this.$mapOverlay.on('click', close);
        this.$mapModal.find('[data-role="map-close"]').on('click', close);
        $(document).on('keydown.aptsMap', (e) => { if (e.key === 'Escape') close(); });
    }

    initMapOnce() {
        if (this.mapInited) return;
        this.ensureMapModal();

        // if the SDK hasn't loaded yet
        if (!window.google || !google.maps) {
            // just mark as initialized — show an empty container
            this.mapInited = true;
            return;
        }

        this.geocoder = new google.maps.Geocoder();
        this.mapBounds = new google.maps.LatLngBounds();

        const centerBasel = { lat: 47.5596, lng: 7.5886 };
        const zoom = (window.BL_MAP && BL_MAP.zoom) ? Number(BL_MAP.zoom) : 13;

        this.map = new google.maps.Map(this.$mapCanvas[0], {
            center: centerBasel,
            zoom,
            streetViewControl: true,
            mapTypeControl: true,
            mapTypeId: google.maps.MapTypeId.ROADMAP
        });

        this.mapInited = true;
    }

    async setMarkersFromAreas(areas = []) {
        if (!this.map) return;
        // remove old ones
        this.mapMarkers.forEach(m => m.setMap(null));
        this.mapMarkers = [];
        this.mapBounds = new google.maps.LatLngBounds();

        // geocoder + request limit
        const geocodeOne = (q) => new Promise((resolve) => {
            // cache
            if (this.geoCache[q]) return resolve(this.geoCache[q]);
            this.geocoder.geocode({ address: q, region: 'CH' }, (res, status) => {
                if (status === 'OK' && res && res[0]) {
                    const loc = res[0].geometry.location;
                    const point = { lat: loc.lat(), lng: loc.lng() };
                    this.geoCache[q] = point;
                    this.saveGeoCache();
                    resolve(point);
                } else {
                    resolve(null);
                }
            });
        });

        for (const a of (areas || [])) {
            const label = a?.name || '';
            if (!label) continue;

            // build the address in the context of the city
            const query = `${label}, Basel, Switzerland`;

            const point = await geocodeOne(query);
            if (!point) continue;

            const marker = new google.maps.Marker({
                position: point,
                map: this.map,
                title: label
            });
            this.mapMarkers.push(marker);
            this.mapBounds.extend(point);

            // click → set the select and trigger search
            marker.addListener('click', async () => {
                this.selectAreaByName(label);          // set select
                // rerun search (push state, same as clicking "Search")
                this.page = 1;
                await this.runSearch(this.uiRef, this.$scopeRef, false, { push: true });
                // optional: close the popup
                this.closeMap();
            });
        }

        if (!this.mapBounds.isEmpty()) {
            this.map.fitBounds(this.mapBounds);
        }
    }

    selectAreaByName(name) {
        const $scope = this.$scopeRef || $(this.sidebarMountSelector).find('[data-role="filters-sidebar"]');
        const $loc = $scope.find('select[data-role="location"]');

        // find option by text (without "(12)")
        let matchVal = null;
        const needle = this.normalizeAreaLabel(name);
        $loc.find('option').each((_, opt) => {
            const txt = ($(opt).text() || '');
            if (this.normalizeAreaLabel(txt) === needle) { matchVal = $(opt).val(); return false; }
        });

        if (matchVal != null) {
            $loc.val(matchVal).trigger('change'); // select2 updates too
        }
    }

    openMap(titleText = t('apartments_in_basel','Apartments in Basel')) {
        this.ensureMapModal();
        if (!this.mapInited) this.initMapOnce();

        this.$mapTitle.text(titleText);
        this.$mapOverlay.removeClass('hidden').attr('data-state','open');
        this.$mapModal.removeClass('hidden').attr('data-state','open').attr('aria-hidden','false');

        // a11y for the button
        $('[data-role="open-map"]').attr({'aria-expanded':'true', 'aria-controls':'bl-map-modal'});

        $('body').addClass('overflow-hidden');
    }

    closeMap() {
        if (!this.$mapModal) return;
        this.$mapOverlay.attr('data-state','closed');
        this.$mapModal.attr('data-state','closed');
        $('[data-role="open-map"]').attr('aria-expanded','false');

        setTimeout(() => {
            this.$mapOverlay.addClass('hidden');
            this.$mapModal.addClass('hidden').attr('aria-hidden','true');
            $('body').removeClass('overflow-hidden');
        }, 0);
    }

    /* ------------------------ RENDER SIDEBAR ------------------------ */
    renderSidebar($mount, filters) {
        const locationOptions = `<option value="">${t('select_location','Select location')}</option>` +
            `<option value="all">${t('select_all','Select all')}</option>` +
            this.buildOptions(filters.areas, a => a.id, a => a.name);
        const guestOptions = this.buildGuestsSelectOptions(filters.guests);
        const bedrooms = (filters.bedrooms || []).sort((a,b)=>a.value-b.value);

        const captionMap = {
            ONE_BEDROOM: `1 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bed w-3 h-3"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>`,
            STUDIO: t('studio','Studio'),
            TWO_BEDROOM: `2 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bed w-3 h-3"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>`,
            THREE_BEDROOM: `3 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bed w-3 h-3"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>`,
            FOUR_BEDROOM: `4 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bed w-3 h-3"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>`
        };

        const WORD2NUM = {
            STUDIO: 0,
            ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5,
            SIX: 6, SEVEN: 7, EIGHT: 8, NINE: 9, TEN: 10
        };

        function bedroomRank(name) {
            const n = String(name || '').toUpperCase();          // e.g. "ONE_BEDROOM"
            if (n.includes('STUDIO')) return 0;                  // studio first
            const first = n.split('_')[0];                       // "ONE"
            return WORD2NUM[first] ?? 999;                       // unknown goes to the end
        }

        const bedroomsSorted = [...bedrooms].sort(
            (a, b) => bedroomRank(a.name) - bedroomRank(b.name)
        );

        const html = `
        <div class="px-4 pb-4 lg:p-6" data-role="filters-sidebar">
          <div class="space-y-4 mb-6">
          
          <div class="hidden lg:flex items-center justify-between">
            <h3 class="text-lg font-semibold text-basel-gray">${t('search_apartments','Search Apartments')}</h3>
            <button data-role="clear-all" class="h-9 rounded-md px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground">${t('clear_all','Clear all')}</button>
          </div>
            
        
            <!-- Location -->
            <div class="select-box">
              <label class="block text-sm font-medium text-basel-gray mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-map-pin w-4 h-4 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
                ${t('location','Location')}
              </label>
              <select data-role="location" class="w-full"></select>
            </div>
        
            <!-- Check-in -->
            <div>
              <label class="block text-sm font-medium text-basel-gray mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-calendar w-4 h-4 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
                ${t('arrival','Arrival Date')}
              </label>
              <input type="text" data-role="checkin" class="w-full h-10 rounded-md border border-input px-3 text-base" placeholder="${t('select_date','Select date')}" />
            </div>
        
            <!-- Check-out -->
            <div>
              <label class="block text-sm font-medium text-basel-gray mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-calendar w-4 h-4 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
                ${t('departure','Departure Date')}
              </label>
            
              <input
                type="text"
                data-role="checkout"
                class="w-full h-10 rounded-md border border-input px-3 text-base"
                placeholder="${t('select_date','Select date')}"
              />
            
              <div class="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-3">
                <div class="flex items-center space-x-2">
                  <label class="flex items-center space-x-2 text-xs sm:text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      name="unsureCheckout"
                      value="true"
                      class="peer h-4 w-4 shrink-0 rounded-sm border border-primary"
                      data-role="unsure-checkout"
                    >
                    <span class="flex-1 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg"
                           width="24"
                           height="24"
                           viewBox="0 0 24 24"
                           fill="none"
                           stroke="currentColor"
                           stroke-width="2"
                           stroke-linecap="round"
                           stroke-linejoin="round"
                           class="lucide lucide-clock w-3 h-3 sm:w-4 sm:h-4 mr-1">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      ${t('checkout_unknown_full', "I'm not sure about check-out date")}
                    </span>
                  </label>
                </div>
              </div>
            </div>
        
            <!-- Guests -->
            <div class="select-box">
              <label class="block text-sm font-medium text-basel-gray mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-users w-4 h-4 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                ${t('guests','Guests')}
              </label>
              <select data-role="guests" class="w-full"></select>
            </div>
        
            <button data-role="search" class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background px-4 py-2 w-full h-10 bg-basel-blue hover:bg-basel-blue-dark text-white font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-search w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
              ${t('search','Search')}
            </button>
          </div>
        
          <div class="shrink-0 bg-border h-[1px] w-full my-6"></div>
        
          <h3 class="text-lg font-semibold text-basel-gray mb-6">${t('filters','Filters')}</h3>
        
          <!-- Stay Type -->
          <div class="mb-6" data-filter="stay-type">
            <h4 class="font-medium text-basel-gray mb-3">${t('stay_type','Stay Type')}</h4>
            <div class="space-y-3" data-role="staytype-list"></div>
          </div>
        
          <div class="shrink-0 bg-border h-[1px] w-full my-6"></div>
        
          <!-- Bedrooms -->
          <div class="mb-6" data-filter="bedrooms">
            <h4 class="font-medium text-basel-gray mb-3 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-bed w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>
              ${t('bedrooms','Bedrooms')}
            </h4>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-2" data-role="bedrooms-buttons">
            ${bedroomsSorted.map(b => {
            const name = String(b.name || '').toUpperCase();
            const caption = captionMap[name] || name;
            return `<button type="button"
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3 text-xs"
                data-role="bedrooms-btn"
                data-value="${this.esc(name)}">${caption}</button>`;
        }).join('')        }
            </div>
          </div>
        
          <div class="shrink-0 bg-border h-[1px] w-full my-6"></div>
        
          <!-- features -->
          <div class="mb-6" data-filter="features">
            <h4 class="font-medium text-basel-gray mb-3">${t('amenities','Amenities')}</h4>
            <div class="space-y-3" data-role="features-list"></div>
          </div>
        </div>`;

        $mount.html(html);

        // fill selects
        const $loc = $mount.find('select[data-role="location"]');
        $loc.html(locationOptions);

        this.areaLabelToValue = {};
        $loc.find('option').each((_, opt) => {
            const raw = ($(opt).text() || '');
            const norm = this.normalizeAreaLabel(raw);
            this.areaLabelToValue[norm] = $(opt).val();
        });

        const $gu = $mount.find('select[data-role="guests"]');
        $gu.html(guestOptions);

        // dynamic lists (features + stay types)
        const $amen = $mount.find('[data-role="features-list"]');
        const orderedFeatures = this._prioritizeFeatures(filters.features || []);
        $amen.html(orderedFeatures.map(a => `
          <label class="flex items-center space-x-2 text-sm text-basel-gray cursor-pointer">
            <input type="checkbox" class="peer h-4 w-4 shrink-0 rounded-sm border border-primary" value="${this.esc(a.id)}" data-role="features">
            <span class="flex-1 flex items-center">
              ${a.iconClass ? `<i class="${a.iconClass}"></i>` : ""}
              <span class="ml-2">${IS_DE ? (this.esc(a.nameLang2) || "") : (this.esc(a.name) || "")}</span>
            </span>
          </label>
        `).join(''));

        const $st = $mount.find('[data-role="staytype-list"]');
        $st.html((filters.stay_types || []).map(st => `
          <label class="flex items-center space-x-2 text-sm text-basel-gray cursor-pointer">
            <input type="checkbox" class="peer h-4 w-4 shrink-0 rounded-sm border border-primary" value="${this.esc(st.type)}" data-role="staytype">
            <span class="flex-1">${this.esc(t(st.type, st.label))}</span>
          </label>
        `).join(''));
    }

    /* ------------------------ ICONS ------------------------ */

    // "Bed Linen & Towels"   -> "bed_linen_&_towels"
    // "USP - Washer/Dryer..." -> "usp_-_washer/dryer_in_unit"
    // "Free Wi-Fi"           -> "free_wi-fi"
    toFeatureId(nameOrId = '') {
        return String(nameOrId)
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_')      // spaces -> underscores
            .replace(/_+/g, '_')       // collapse multiple _
            .replace(/^_+|_+$/g, '');  // trim _
    }

    /* ------------------------ INIT WIDGETS ------------------------ */
    initDatepickers($container, cfg = { minStayDays: 1 }) {
        const $ci = $container.find('[data-role="checkin"]');
        const $co = $container.find('[data-role="checkout"]');
        const today = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();

        // Hosts for inline/static calendars (so popups are anchored to inputs)
        const ciHost = $ci.parent().addClass('relative')[0];
        const coHost = $co.parent().addClass('relative')[0];

        // Ensure mobile-friendly placement (tries "below", otherwise center overlay)
        const ensureMobilePlacement = (inst) => {
            // Only on mobile breakpoint
            if (!window.matchMedia('(max-width: 1023px)').matches) return;

            // Prefer showing above
            inst.set('position', 'below');

            const cal = inst.calendarContainer;
            cal.classList.remove('fp-center'); // reset previous state

            // Measure if it actually fits above; if not — center
            requestAnimationFrame(() => {
                const ch = cal.getBoundingClientRect().height || 0;
                const topAvail = inst._positionElement.getBoundingClientRect().top;
                if (ch + 16 > topAvail) {
                    cal.classList.add('fp-center');
                }
            });
        };

        flatpickr.localize({
            ...(IS_DE ? German : {}),
            firstDayOfWeek: 1
        });

        const ciPicker = flatpickr($ci[0], {
            allowInput: true,
            dateFormat: 'Y-m-d',
            defaultDate: this.dates?.checkIn || null,
            disableMobile: true,
            minDate: today,
            static: true,                 // render inline, anchored to input
            appendTo: ciHost,             // mount inside input's parent
            positionElement: $ci[0],
            position: 'above',
            onOpen: [
                (sel, str, inst) => {
                    if (!inst.selectedDates.length) inst.jumpToDate(inst.config.minDate || today);
                    ensureMobilePlacement(inst);
                }
            ],
            onClose: [ (sel, str, inst) => inst.calendarContainer.classList.remove('fp-center') ]
        });

        const coPicker = flatpickr($co[0], {
            // --- options only (do not touch onChange wiring) ---
            allowInput: true,
            dateFormat: 'Y-m-d',
            defaultDate: this.dates?.checkOut || null,
            disableMobile: true,
            minDate: today,
            static: true,
            appendTo: coHost,
            positionElement: $co[0],
            position: 'above',
            onOpen: [ (_, __, inst) => {
                const ci = ciPicker.selectedDates[0];
                const minCoDate = ci ? this.addDays(ci, cfg.minStayDays) : today;
                coPicker.set('minDate', minCoDate);
                const sel = coPicker.selectedDates[0];
                coPicker.jumpToDate(sel && sel >= minCoDate ? sel : minCoDate);
                ensureMobilePlacement(inst);
            }],
            onClose: [ (sel, str, inst) => inst.calendarContainer.classList.remove('fp-center') ]
        });

        const reconfigCO = () => {
            const ci = ciPicker.selectedDates[0];
            if (ci) {
                const minCoDate = this.addDays(ci, cfg.minStayDays);
                coPicker.set('minDate', minCoDate);
                const sel = coPicker.selectedDates[0];
                if (sel && sel < minCoDate) { coPicker.clear(); $co.trigger('change'); }
                if (coPicker.isOpen) coPicker.jumpToDate(sel && sel >= minCoDate ? sel : minCoDate);
            } else {
                coPicker.set('minDate', today);
                if (coPicker.isOpen) coPicker.jumpToDate(today);
            }
        };

        ciPicker.config.onChange.push(() => { reconfigCO(); $ci.trigger('change'); });
        reconfigCO();

        $ci.on('change', () => this.updateClearAllButtons($container));
        $co.on('change', () => {
            if (this._suppressCheckoutChange) return;

            this.updateClearAllButtons($container);

            if ($co.val() || '') {
                const $unsureCheckout = $container.find('[data-role="unsure-checkout"]');
                $unsureCheckout.prop('checked', false);
            }

            this.toggleUnsureCheckoutUI($container);
        });
    }

    initSelects($scope) {
        const self = this;
        const initOne = ($el, placeholder) => {
            if ($el.hasClass('select2-hidden-accessible')) $el.select2('destroy');
            $el.select2({
                minimumResultsForSearch: $el.is('[data-role="guests"]') ? Infinity : 0,
                width: '100%',
                dropdownParent: $el.closest('.select-box'),
                dropdownAutoWidth: true,
                placeholder,
                theme: 'basel'
            });
        };
        const $loc = $scope.find('select[data-role="location"]');
        const $gus = $scope.find('select[data-role="guests"]');
        initOne($loc, t('select_location','Select location'));
        initOne($gus, t('select_guests','Select guests'));
        $gus.on('change', function () {
            const v = parseInt($(this).val(), 10);
            self.guests = Number.isFinite(v) ? v : null;
        });
        $loc.on('change', () => self.updateClearAllButtons($scope));
        $gus.on('change', () => self.updateClearAllButtons($scope));
    }

    /* ------------------------ API ------------------------ */
    getAllFilters() {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${this.proxyUrl}?type=getAllFilters`,
                method: 'POST',
                contentType: 'application/json',
                dataType: 'json',
                success: (response) => resolve(response?.data ?? response),
                error: (xhr) => { console.log(xhr.responseText); reject('Validation errors occurred'); }
            });
        });
    }

    // Build params for /api/apartments (see swagger)
    buildSearchParams($scope) {
        // area name (swagger expects name, not id)
        const $loc = $scope.find('select[data-role="location"]');

        const locVal = ($loc.val() || '').toString();
        const isAll = (locVal === '' || locVal === 'all');


        const area = (!isAll)
            ? ($loc.find('option:selected').text() || '').replace(/\s*\(\d+\)\s*$/, '').trim()
            : null;

        const guestsDisplay = ($scope.find('select[data-role="guests"]').val() || '').toString() || null;

        // Map UI guests -> API cap
        let maxGuests = undefined;
        if (guestsDisplay) {
            const mapped = this.guestsMap?.[guestsDisplay];
            maxGuests = Number.isFinite(Number(mapped)) ? Number(mapped) : Number(guestsDisplay);
        }

        const dateFrom = $scope.find('[data-role="checkin"]').val() || null;
        const isUnsureCheckout = this.isUnsureCheckoutChecked($scope);

        let manualDateTo = $scope.find('[data-role="checkout"]').val() || null;

        if (isUnsureCheckout) {
            manualDateTo = null;
        }

        let dateTo = manualDateTo;
        if (isUnsureCheckout && dateFrom) {
            const arrivalDate = this.parseDateYMD(dateFrom);
            if (arrivalDate) {
                dateTo = this.formatDateYMD(this.addDays(arrivalDate, this.getUnsureCheckoutDays()));
            }
        }

        // Bedrooms — mutually exclusive selection
        const $bActive = $scope.find('[data-role="bedrooms-btn"].data-active').first();
        const bedrooms = $bActive.length ? String($bActive.data('value')) : '';

        // features -> comma-separated (include extra numeric ids from URL)
        const checkedIds = $scope.find('input[data-role="features"]:checked')
            .map((_, el) => String($(el).val()))
            .get();

        const merged = [...checkedIds, ...(this.extraFeatureIds || [])]
            .map(v => String(v).trim())
            .filter(v => /^\d+$/.test(v)); // only numeric
        // unique, keep order
        const uniq = [];
        const seen = new Set();
        merged.forEach(v => { if (!seen.has(v)) { seen.add(v); uniq.push(v); } });

        const features = uniq.length ? uniq.join(',') : null;

        // Stay type — single value
        const type = $scope.find('input[data-role="staytype"]:checked').map((_,el)=>String($(el).val())).get()[0] || null;

        const availability = (dateFrom && dateTo) ? 'true' : undefined;

        return {
            page: this.page,
            limit: this.pageSize,
            area: area || undefined,
            bedrooms: bedrooms ?? undefined,

            // URL wants display (1..N)
            guests: guestsDisplay ? Number(guestsDisplay) : undefined,

            // API wants mapped cap
            maxGuests,

            stayType: type || undefined,
            features: features || undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            availability,
            unsureCheckout: isUnsureCheckout ? 'true' : undefined,
            _isUnsureCheckout: isUnsureCheckout,
            _hasManualDateTo: !!manualDateTo,
            ids: this.idsCSV || undefined,

            // NEW: keep and forward unitType
            unitType: (this.unitType || undefined),
        };
    }

    // Apartments request via proxy (proxy forwards to GET /api/apartments)
    getApartments(params) {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${this.proxyUrl}?type=getApartments`,
                method: 'POST',
                contentType: 'application/json',
                dataType: 'json',
                data: JSON.stringify(params || {}),
                success: (response) => {
                    const data = response?.data ?? [];

                    // PRIMARY: pagination.total
                    const pgTotal    = response?.pagination?.total;
                    const metaUnits  = response?.metadata?.totalUnits; // fallback
                    const metaTotal  = response?.meta?.total;
                    const plainTotal = response?.total;
                    const hdr        = response?.headers?.['X-Total-Count'];
                    const hdrNum     = hdr != null ? Number(hdr) : null;

                    // the first valid candidate becomes total
                    const candidates = [pgTotal, metaUnits, metaTotal, plainTotal, hdrNum];
                    let total = null;
                    for (const t of candidates) {
                        const n = Number(t);
                        if (Number.isFinite(n)) { total = n; break; }
                    }

                    resolve({ data, total });
                },
                error: (xhr) => { console.log(xhr.responseText); reject('Failed to load apartments'); }
            });
        });
    }

    /* ------------------------ RESULTS RENDERING ------------------------ */
    renderResultsShell() {
        const $mount = $(this.resultsMountSelector);
        const html = `
        <div data-role="results-shell">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <div>
              <h2 class="text-lg sm:text-xl font-semibold text-basel-gray" data-role="results-title">${t('apartments_title','Apartments')}</h2>
              <p class="text-sm sm:text-base text-gray-600">${t('apartments_subtitle','Perfect for your stay')}</p>
            </div>
            <div class="flex items-center justify-between sm:justify-end space-x-2">
              <button data-role="open-filter-popup" class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3 lg:hidden flex-1 sm:flex-initial" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-sliders-horizontal w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="21" x2="14" y1="4" y2="4"></line><line x1="10" x2="3" y1="4" y2="4"></line><line x1="21" x2="12" y1="12" y2="12"></line><line x1="8" x2="3" y1="12" y2="12"></line><line x1="21" x2="16" y1="20" y2="20"></line><line x1="12" x2="3" y1="20" y2="20"></line><line x1="14" x2="14" y1="2" y2="6"></line><line x1="8" x2="8" y1="10" y2="14"></line><line x1="16" x2="16" y1="18" y2="22"></line></svg>
                ${t('filters','Filters')}
              </button>
              <div class="hidden sm:flex border border-gray-200 rounded-lg overflow-hidden">
                <button data-role="view-grid" class="inline-flex items-center justify-center gap-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-9 rounded-none px-3" title="${t('grid_view','Grid view')}">
                  <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-grid3x3 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9h18"></path><path d="M3 15h18"></path><path d="M9 3v18"></path><path d="M15 3v18"></path></svg>
                </button>
                <button data-role="view-list" class="inline-flex items-center justify-center gap-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground h-9 rounded-none px-3" title="${t('list_view','List view')}">
                  <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-list w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12h.01"></path><path d="M3 18h.01"></path><path d="M3 6h.01"></path><path d="M8 12h13"></path><path d="M8 18h13"></path><path d="M8 6h13"></path></svg>
                </button>
              </div>
            </div>
          </div>
        
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-role="results-grid"></div>
        
          <div class="text-center mt-8" data-role="loadmore-wrap" style="display:none;">
            <button data-role="loadmore" class="inline-flex items-center justify-center gap-2 text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-11 rounded-md px-8">
              ${t('load_more_apartments','Load More Apartments')}
            </button>
          </div>
        </div>`;
        $mount.html(html);
        return {
            $shell: $mount.find('[data-role="results-shell"]'),
            $title: $mount.find('[data-role="results-title"]'),
            $grid : $mount.find('[data-role="results-grid"]'),
            $loadWrap: $mount.find('[data-role="loadmore-wrap"]'),
            $loadBtn : $mount.find('[data-role="loadmore"]'),
            $btnGrid: $mount.find('[data-role="view-grid"]'),
            $btnList: $mount.find('[data-role="view-list"]')
        };
    }

    card(unit, idx) {
        const loc = [unit?.addressLine1, unit?.areaName, unit?.city]
            .map(v => (v ?? '').toString().trim())
            .filter(v => v.length > 0)
            .join(', ');
        const base = '/wp-content/themes/basellodging-theme/imgs';
        const placeholder = base ? base + "/placeholder.svg" : "/placeholder.svg";
        const img = (unit.mainImage && unit.mainImage.url) || placeholder;
        const sim = unit?.similarUnits?.count || 0;
        const bedrooms = unit?.specifications?.numberOfBedrooms;
        const bedroomsLabel = (bedrooms === 0 || bedrooms === null) ? t('studio','Studio') : (bedrooms ?? '');
        const guests = unit?.specifications?.maxOccupancy;
        const areaMin = unit?.specifications?.area?.min;
        const areaMax = unit?.specifications?.area?.max;
        const unitName = unit?.unitName;
        const areaLabel = (areaMin && areaMax && areaMin !== areaMax) ? `${areaMin}–${areaMax}m²`
            : (areaMin || areaMax) ? `${areaMin || areaMax}m²` : null;

        const features = Array.isArray(unit.features)
            ? this._prioritizeFeatures(unit.features).slice(0, 3)
            : [];

        const sp = new URLSearchParams(window.location.search);
        const forceDaily = sp.get('stayType') === 'DAILY';

        let pricingToUse;
        if (Array.isArray(unit.pricing)) {
            pricingToUse = forceDaily ? (unit.pricing[1] || unit.pricing[0]) : unit.pricing[0];
        } else {
            pricingToUse = unit.pricing;
        }
        const priceHtml = this.priceLabel(pricingToUse);

        // externalId in response is a number; index keys are strings
        const extId = String(unit.externalId ?? unit.id ?? '');
        const local  = this.localIndex?.[extId];

        // Card title: priority — WP post title, else backend
        const title = local?.title || (unit.unitName ? `${unit.unitName}` : (unit.referenceNumber || t('apartment','Apartment')));

        const rawDetailUrl = local?.url || '#';
        const detailUrl = this.addForwardParams(rawDetailUrl);

        return `
<a href="${detailUrl}" class="rounded-lg bg-card text-card-foreground overflow-hidden border-0 shadow-md h-full flex flex-col apartment-item">
  <div class="relative">
    <img src="${this.esc(img)}" alt="${this.esc(title)}" class="w-full h-48 sm:h-52 md:h-48 object-cover" loading="lazy">
    ${sim>0 ? `<div class="absolute top-3 left-3 flex flex-col gap-2">
      <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-[#001F39] text-white">
        ${sim} ${t('similar_available','similar available')}
      </div>
    </div>` : ``}
  </div>
  <div class="p-4 sm:p-5 md:p-4 flex flex-col flex-1">
    <div class="flex items-start justify-between mb-2">
        <div class="flex items-start text-xs sm:text-sm text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-map-pin w-3 h-3 sm:w-4 sm:h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
          <span class="">${this.esc(loc)}</span>
        </div>
        <div class="flex items-center"><span class="px-1 py-0.5 text-xs font-semibold text-foreground text-basel-blue">ID:</span><div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground bg-white text-basel-blue border-basel-blue hover:bg-white hover:text-basel-blue">${this.esc(unitName)}</div></div>
    </div>
    <h3 class="font-semibold text-sm sm:text-base text-basel-gray mb-2 line-clamp-2 leading-tight">${this.esc(title)}</h3>

    <div class="flex items-center gap-x-4 mb-3 text-sm text-gray-600">
      ${guests != null ? `
        <div class="flex items-center space-x-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-users w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
          <span>${this.esc(guests)}</span>
        </div>` : ``}
      ${bedroomsLabel !== '' ? `
        <div class="flex items-center space-x-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-bed w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>
          <span>${this.esc(bedroomsLabel)}</span>
        </div>` : ``}
      ${areaLabel ? `
        <div class="flex items-center space-x-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="lucide lucide-square w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2"></rect></svg>
          <span>${this.esc(areaLabel)}</span>
        </div>` : ``}
    </div>

    <div class="flex items-center space-x-2 mb-3 flex-wrap gap-y-1">
      ${features.map(a => `
        <div class="flex items-center space-x-1 text-xs text-gray-600">
          ${a.iconClass? `<i class="${a.iconClass}"></i>` : ""}
          <span class="hidden sm:inline">${IS_DE ? (this.esc(a.nameLang2) || "") : (this.esc(a.name) || "")}</span>
        </div>
      `).join('')}
    </div>

    <div class="flex items-center justify-between pt-2 border-t border-gray-100 mt-auto">
      <div class="flex items-baseline space-x-1">
        <span class="text-base sm:text-lg font-bold text-basel-gray">${priceHtml}</span>
      </div>
        <button class="inline-flex items-center justify-center gap-2 font-medium rounded-md bg-basel-blue text-white hover:bg-basel-blue/90 text-xs sm:text-sm px-3 sm:px-4 h-8 sm:h-9">
            <span class="hidden sm:inline">${t('view_details','View Details')}</span><span class="sm:hidden">${t('view','View')}</span>
        </button>
    </div>
  </div>
</a>`;
    }

    renderUnits($grid, units, append=false) {
        const cards = (units || []).map((u,i)=>this.card(u,i)).join('');
        if (append) $grid.append(cards);
        else $grid.html(cards || `<div class="col-span-full text-left text-gray-500 py-12">${t('no_apartments_found','No apartments found')}</div>`);

        this.applyViewToCards($grid);
    }
    updateHeader($title, total, locText = 'Basel') {
        const count = Number.isFinite(total) ? total : 0;
        $title.text(`${count} ${t('apartments_in_basel_min','apartments in Basel')}`);
    }
    updateLoadMore($wrap, $btn, shown, total, onClick) {
        // ensure the message exists, but keep it hidden by default
        let $msg = $wrap.find('[data-role="no-more"]');
        if (!$msg.length) {
            $msg = $(
                `<div data-role="no-more" class="text-center mt-8 text-gray-500" style="display:none;">
                  <p>${t('seen_all_apartments',"You've seen all available apartments")}</p>
                </div>`
            );
            $msg.insertAfter($btn);
        }

        const tot = Number(total ?? NaN);
        const shn = Number(shown ?? 0);

        // 0 results or invalid total -> hide the entire block
        if (!Number.isFinite(tot) || tot <= 0) {
            $msg.hide();
            $btn.hide().off('click');
            $wrap.hide();
            return;
        }

        // more to load
        if (shn < tot) {
            const remaining = tot - shn;
            $wrap.show();
            $msg.hide();
            $btn
                .show()
                .html(`${t('load_more_apartments','Load More Apartments')}${remaining ? ` (${remaining} ${t('remaining','remaining')})` : ''}`)
                .off('click')
                .on('click', onClick);
            return;
        }

        // no more — show only the message
        $wrap.show();
        $btn.hide().off('click');
        $msg.show();
    }

    /* ------------------------ VIEW MODE HELPERS ------------------------ */
    readStoredView() {
        const v = (typeof window !== 'undefined') ? localStorage.getItem(this.viewStorageKey) : null;
        return (v === 'list' || v === 'grid') ? v : 'grid';
    }
    storeView(v) {
        try { localStorage.setItem(this.viewStorageKey, v); } catch (_) {}
    }
    applyViewToContainer(ui) {
        // results container
        if (this.view === 'list') {
            ui.$grid.removeClass('grid grid-cols-1 md:grid-cols-2')
                .addClass('flex flex-col gap-4 sm:gap-6');
        } else {
            ui.$grid.removeClass('flex flex-col')
                .addClass('grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6');
        }
    }
    applyViewButtons(ui) {
        const active   = 'bg-primary text-primary-foreground hover:bg-primary/90';
        const inactive = 'hover:bg-accent hover:text-accent-foreground';
        if (this.view === 'grid') {
            ui.$btnGrid.addClass(active).removeClass(inactive);
            ui.$btnList.addClass(inactive).removeClass(active);
        } else {
            ui.$btnList.addClass(active).removeClass(inactive);
            ui.$btnGrid.addClass(inactive).removeClass(active);
        }
    }
    applyViewToCards($grid) {
        // card differences between grid and list
        const listCls = 'sm:flex-row min-h-[240px] sm:h-64';
        const gridCls = 'h-full';
        const $cards = $grid.children('div.rounded-lg');
        const $imgBox = $grid.find('div.relative');
        const imgBoxListCls = 'w-full sm:w-80 h-48 sm:h-auto flex-shrink-0';
        const $img = $grid.find('div.relative img');
        const imgListCls = 'h-full';
        const imgGridCls = 'h-48 sm:h-52 md:h-48';
        if (this.view === 'list') {
            $cards.addClass(listCls).removeClass(gridCls);
            $imgBox.addClass(imgBoxListCls);
            $img.addClass(imgListCls).removeClass(imgGridCls);
        } else {
            $cards.addClass(gridCls).removeClass(listCls);
            $imgBox.removeClass(imgBoxListCls);
            $img.addClass(imgGridCls).removeClass(imgListCls);
        }
    }

    // Read params from URL, apply to UI, remove invalid ones
    applyUrlToUI($scope, filters) {
        if (typeof window === 'undefined') return;

        const sp = new URLSearchParams(window.location.search);
        let changed = false;

        // legacy migration: ?maxGuests= -> ?guests=
        if (sp.has('maxGuests') && !sp.has('guests')) {
            sp.set('guests', sp.get('maxGuests'));
            sp.delete('maxGuests');
            changed = true;
        }

        // Remove all keys that are not in ALLOWED_KEYS
        for (const k of Array.from(sp.keys())) {
            if (!this.constructor.ALLOWED_KEYS.includes(k)) { sp.delete(k); changed = true; }
        }

        const $loc = $scope.find('select[data-role="location"]');
        const $gus = $scope.find('select[data-role="guests"]');
        const $bedBtns = $scope.find('[data-role="bedrooms-buttons"] [data-role="bedrooms-btn"]');
        const $amenList = $scope.find('input[data-role="features"]');
        const $stayList = $scope.find('input[data-role="staytype"]');
        const $ci = $scope.find('[data-role="checkin"]');
        const $co = $scope.find('[data-role="checkout"]');
        const $unsureCheckout = $scope.find('[data-role="unsure-checkout"]');

        // area (by name, because select value is id)
        const areaParam = sp.get('area');
        if (areaParam) {

            if (areaParam.trim().toLowerCase() === 'all') {
                $loc.val('all').trigger('change');
            } else {
                let matched = null;
                $loc.find('option').each(function () {
                    const txt = ($(this).text() || '').replace(/\s*\(\d+\)\s*$/, '').trim();
                    if (txt.toLowerCase() === areaParam.trim().toLowerCase()) { matched = this; return false; }
                });
                if (matched) { $loc.val($(matched).val()).trigger('change'); }
                else { sp.delete('area'); changed = true; }
            }
        }

        // bedrooms (buttons)
        const br = sp.get('bedrooms');
        if (br) {
            const brNum = String(br);
            const valid = (filters?.bedrooms || []).some(b => String(b.name) === brNum);
            if (valid) {
                $bedBtns.removeClass('bg-accent text-accent-foreground data-active');
                $bedBtns.filter(`[data-value="${brNum}"]`).addClass('bg-accent text-accent-foreground data-active');
            } else { sp.delete('bedrooms'); changed = true; }
        }

        // guests (select)
        const mg = sp.get('guests');
        if (mg !== null) {
            const existsInDom = $gus.find(`option[value="${$.escapeSelector(String(mg))}"]`).length > 0;
            if (existsInDom) {
                $gus.val(String(mg)).trigger('change');
            } else {
                sp.delete('guests'); changed = true;
            }
        }

        // type (stay type, single checkbox)
        const tp = sp.get('stayType');
        if (tp) {
            const valid = (filters?.stay_types || []).some(st => String(st.type) === String(tp));
            if (valid) {
                $stayList.prop('checked', false);
                $stayList.filter(`[value="${tp}"]`).prop('checked', true);
            } else { sp.delete('stayType'); changed = true; }
        }

        // features (csv)
        // features (csv) — allow ANY numeric ids, do not delete unknown ones
        this.extraFeatureIds = [];

        const am = sp.get('features');
        if (am) {
            const raw = am.split(',').map(s => String(s).trim()).filter(Boolean);

            // keep only numeric
            const numeric = [];
            const seen = new Set();
            raw.forEach(v => {
                if (/^\d+$/.test(v) && !seen.has(v)) { seen.add(v); numeric.push(v); }
            });

            // reset all known checkboxes
            $amenList.prop('checked', false);

            // check those that exist in DOM; store the rest as extras
            numeric.forEach(id => {
                const $cb = $amenList.filter(`[value="${id}"]`);
                if ($cb.length) $cb.prop('checked', true);
                else this.extraFeatureIds.push(id);
            });

            // normalize URL: numeric-only, de-duped, keep order
            if (!numeric.length) {
                sp.delete('features');
                changed = true;
            } else if (numeric.join(',') !== am) {
                sp.set('features', numeric.join(','));
                changed = true;
            }
        } else {
            this.extraFeatureIds = [];
        }

        // Keep unitType from URL even if we don't have a UI control for it
        if (sp.has('unitType')) {
            const ut = sp.get('unitType');
            this.unitType = ut || null;
            // don't delete it here — it's allowed and should be forwarded
        } else {
            this.unitType = null;
        }

        // Check-out unknown flag
        const hasUnsureCheckout = sp.has('unsureCheckout');

        if (hasUnsureCheckout) {
            const v = String(sp.get('unsureCheckout') || '').toLowerCase();

            if (!['true', '1', 'yes'].includes(v)) {
                sp.delete('unsureCheckout');
                changed = true;
                $unsureCheckout.prop('checked', false);
            } else {
                if (v !== 'true') {
                    sp.set('unsureCheckout', 'true');
                    changed = true;
                }

                $unsureCheckout.prop('checked', true);

                // If check-out is unknown, remove manual departure date from URL.
                if (sp.has('dateTo')) {
                    sp.delete('dateTo');
                    changed = true;
                }

                if (sp.has('availability')) {
                    sp.delete('availability');
                    changed = true;
                }

                $co.val('').trigger('change');
            }
        } else {
            $unsureCheckout.prop('checked', false);
        }

        // dates + availability
        const reDate = /^\d{4}-\d{2}-\d{2}$/;
        const df = sp.get('dateFrom');
        const hasValidUnsureCheckout = sp.get('unsureCheckout') === 'true';

        // unsureCheckout has priority over any dateTo from URL
        if (hasValidUnsureCheckout && sp.has('dateTo')) {
            sp.delete('dateTo');
            changed = true;
        }

        const dt = hasValidUnsureCheckout ? null : sp.get('dateTo');

        let dfOk = !!df && reDate.test(df);
        let dtOk = !!dt && reDate.test(dt);

        if (df) {
            if (dfOk) {
                $ci.val(df).trigger('change');
            } else {
                sp.delete('dateFrom');
                changed = true;
                dfOk = false;
            }
        }

        if (hasValidUnsureCheckout) {
            const fp = $co[0] && $co[0]._flatpickr;

            if (fp) {
                fp.clear();
                fp.close();
            }

            $co.val('');
            dtOk = false;
        } else if (dt) {
            if (dtOk) {
                $co.val(dt).trigger('change');
            } else {
                sp.delete('dateTo');
                changed = true;
                dtOk = false;
            }
        }

        // keep 'availability' from URL only if both dates are valid and value === 'true'/'1'
        if (sp.has('availability')) {
            const av = sp.get('availability');
            if (!(dfOk && dtOk && (av === 'true' || av === '1'))) { sp.delete('availability'); changed = true; }
        }

        // Final sync. URL state has priority on page load.
        $unsureCheckout.prop('checked', hasValidUnsureCheckout);
        this.toggleUnsureCheckoutUI($scope);

        // Update URL if something changed/cleaned (no '?' when empty)
        if (changed) {
            const qs = sp.toString();
            const newUrl = window.location.pathname + (qs ? `?${qs}` : '');
            history.replaceState(null, '', newUrl);
        }

        this.updateClearAllButtons($scope);
    }


    // --- URL sync (no '?' when empty; no limit/page at all) ---
    updateUrl(params, { push = false } = {}) {
        if (typeof window === 'undefined' || !window.history?.replaceState) return;

        const url = new URL(window.location.href);
        const sp  = url.searchParams;

        // completely remove these keys from URL
        sp.delete('page');
        sp.delete('limit');
        sp.delete('maxGuests');

        // Special: if Location select is "all", keep it in URL as area=all
        const $scope = this.$scopeRef || $(this.sidebarMountSelector).find('[data-role="filters-sidebar"]');
        const $loc = $scope.find('select[data-role="location"]');
        const locVal = String($loc.val() || '').trim();

        // only our filters (without page/limit and without view)
        const KEYS = this.constructor.ALLOWED_KEYS;

        const isUnsureCheckout = params._isUnsureCheckout === true || params.unsureCheckout === 'true';
        const hasManualDateTo = params._hasManualDateTo === true;

        KEYS.forEach((k) => {
            if (isUnsureCheckout && !hasManualDateTo && (k === 'dateTo' || k === 'availability')) {
                sp.delete(k);
                return;
            }

            if (k === 'unsureCheckout' && hasManualDateTo) {
                sp.delete(k);
                return;
            }

            const v = (k === 'guests') ? params.guests : params[k];
            if (v === undefined || v === null || v === '') sp.delete(k);
            else sp.set(k, String(v));
        });

        if (locVal === 'all') sp.set('area', 'all');

        // view is stored only in localStorage
        // sp.set('view', this.view);

        const qs = sp.toString();
        const newUrl = url.pathname + (qs ? `?${qs}` : '');
        history[push ? 'pushState' : 'replaceState'](null, '', newUrl);
    }

    hasActiveFilters($scope) {
        const hasBedrooms = $scope.find('[data-role="bedrooms-btn"].data-active').length > 0;
        const hasAmen     = $scope.find('input[data-role="features"]:checked').length > 0;
        const hasStay     = $scope.find('input[data-role="staytype"]:checked').length > 0;
        const hasCheckin     = $scope.find('input[data-role="checkin"]').val();
        const hasCheckout     = $scope.find('input[data-role="checkout"]').val();
        const hasUnsureCheckout = $scope.find('input[data-role="unsure-checkout"]').is(':checked');
        const hasLocation     = $scope.find('select[data-role="location"]').val();
        const hasGuests     = $scope.find('select[data-role="guests"]').val();

        const hasExtraAmen = Array.isArray(this.extraFeatureIds) && this.extraFeatureIds.length > 0;

        return hasBedrooms || hasAmen || hasStay || hasExtraAmen || hasLocation || hasCheckin || hasCheckout || hasUnsureCheckout || hasGuests;
    }

    updateClearAllButtons($scope) {
        const active = this.hasActiveFilters($scope);
        $('[data-role="clear-all"]').toggleClass('clear-hidden', !active);
    }

    /* === Mobile filters: swipe-to-close === */
    initMobileFilterSwipe() {
        const sheet   = document.getElementById('mobile-popup-filter');
        const overlay = document.getElementById('mobile-filter-overlay');
        const scroller = document.getElementById('sidebar-filters-mount') || sheet;
        const handle   = sheet?.querySelector('.popup-header') || sheet;

        if (!sheet || !overlay) return;

        // helper: clean close with animation and class cleanup
        const animateClose = () => {
            const end = () => {
                sheet.style.transition = '';
                sheet.style.transform  = '';
                sheet.style.willChange = '';
                overlay.style.transition = '';
                overlay.style.opacity    = '';

                sheet.classList.remove('data-active');
                overlay.classList.remove('data-active');
                $('[data-role="open-filter-popup"]').removeClass('data-active');

                sheet.removeEventListener('transitionend', end);
            };

            // animate down, fade overlay
            sheet.style.transition = 'transform 220ms cubic-bezier(0.22,1,0.36,1)';
            sheet.style.transform  = 'translateY(100%)';
            overlay.style.transition = 'opacity 200ms ease';
            overlay.style.opacity    = '0';

            sheet.addEventListener('transitionend', end, { once: true });
        };

        let dragging=false, startY=0, lastY=0, lastT=0, velocity=0;

        const getY = e => (e.touches ? e.touches[0].clientY : e.clientY);

        const onStart = (e) => {
            // work only when the popup is shown
            if (!sheet.classList.contains('data-active')) return;

            // start from header or when scroller is at top
            const fromHeader = handle.contains(e.target);
            const canGrab = fromHeader || (scroller.scrollTop <= 0);
            if (!canGrab) return;

            dragging = true;
            startY = lastY = getY(e); lastT = performance.now(); velocity = 0;

            sheet.style.transition = 'none';
            sheet.style.willChange = 'transform';

            // instantly update overlay opacity while dragging
            overlay.style.transition = 'none';
            // ensure we start from full opacity
            overlay.style.opacity = '1';

            window.addEventListener('touchmove', onMove, { passive: false });
            window.addEventListener('mousemove',  onMove, { passive: false });
            window.addEventListener('touchend',   onEnd,  { passive: true  });
            window.addEventListener('mouseup',    onEnd,  { passive: true  });
        };

        const onMove = (e) => {
            if (!dragging) return;
            const y = getY(e);
            let dy = y - startY;

            // pull only downward; upward — apply resistance
            if (dy < 0) dy *= 0.2;
            else e.preventDefault(); // block native scroll

            const translate = Math.max(0, dy);
            sheet.style.transform = `translateY(${translate}px)`;

            // 👇 proportionally reduce overlay opacity based on drag progress (0..1)
            const h = sheet.offsetHeight || window.innerHeight || 1;
            const progress = Math.min(1, Math.max(0, translate / h)); // 0..1
            overlay.style.opacity = String(1 - progress);

            const now = performance.now();
            velocity = (y - lastY) / Math.max(1, now - lastT); // px/ms
            lastY = y; lastT = now;
        };

        const onEnd = () => {
            if (!dragging) return;
            dragging = false;

            const ty = (() => {
                const m = new DOMMatrixReadOnly(getComputedStyle(sheet).transform);
                return m.m42 || 0;
            })();

            const height = sheet.offsetHeight || 1;
            const draggedEnough = ty > height * 0.33 || Math.abs(velocity) > 0.5;

            // if dragged sufficiently — close; otherwise snap back
            if (draggedEnough) {
                animateClose();
            } else {
                sheet.style.transition = 'transform 180ms ease';
                sheet.style.transform  = 'translateY(0)';
                // bring the overlay opacity back
                overlay.style.transition = 'opacity 180ms ease';
                overlay.style.opacity    = '1';
                setTimeout(() => {
                    sheet.style.transition=''; sheet.style.willChange='';
                    overlay.style.transition='';
                }, 180);
            }

            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('mousemove',  onMove);
            window.removeEventListener('touchend',   onEnd);
            window.removeEventListener('mouseup',    onEnd);
        };

        // start from the handle and from the scroll area (when at top)
        handle.addEventListener('touchstart', onStart, { passive: true });
        handle.addEventListener('mousedown',  onStart, { passive: true });
        scroller.addEventListener('touchstart', onStart, { passive: true });
        scroller.addEventListener('mousedown',  onStart, { passive: true });

        // click on overlay or on the close button
        $('body').on('click', '#mobile-filter-overlay, [data-role="close-popup"]', (e) => {
            e.preventDefault();
            animateClose();
        });
    }




    /* ------------------------ BOOT ------------------------ */
    async boot() {
        const $filtersMount = $(this.sidebarMountSelector);
        const $results = $(this.resultsMountSelector);
        if (!$filtersMount.length || !$results.length) return;

        try {
            // 1) fetch our WP apartments index
            await this.getLocalIndex();

            // 2) filters from microservice
            const filters = await this.getAllFilters();
            this.renderSidebar($filtersMount, filters);

            const $scope = $filtersMount.find('[data-role="filters-sidebar"]');
            this.$scopeRef = $scope;
            this.initSelects($scope);
            this.initDatepickers($scope, { minStayDays: 1 });

            await this.initAddressAutocomplete();

            this.applyUrlToUI($scope, filters);

            this.toggleUnsureCheckoutUI($scope);

            this.updateClearAllButtons($scope);

            // Results shell
            const ui = this.renderResultsShell();
            this.uiRef = ui;


            this.ensureMapModal();
            this.initMapOnce();

            this.initMobileFilterSwipe();

            $(document).on('click', '[data-role="open-map"]', (e) => {
                e.preventDefault();

                const $opt = $scope.find('select[data-role="location"] option:selected');
                const city = $opt.val()
                    ? ($opt.text() || 'Basel').replace(/\s*\(\d+\)\s*$/, '')
                    : 'Basel';

                this.openMap(`${t('apartments_in','Apartments in')} ${city}`);

                // on resize -> focus on address first; else fitBounds; else fallback to Basel
                setTimeout(async () => {
                    if (!this.map) return;
                    google.maps.event.trigger(this.map, 'resize');

                    // 1) address search
                    if (await this.focusMapToSearch()) return;

                    // 2) if there are area markers -> fitBounds
                    if (this.mapBounds && !this.mapBounds.isEmpty()) {
                        this.map.fitBounds(this.mapBounds);
                        return;
                    }

                    // 3) fallback — default to Basel
                    const centerBasel = { lat: 47.5596, lng: 7.5886 };
                    this.map.setCenter(centerBasel);
                    this.map.setZoom(window.BL_MAP?.zoom ?? 13);
                }, 80);
            });

            // if markers are not built yet — build them right away
            if (filters?.areas?.length) {
                // safe async call (geocoder)
                this.setMarkersFromAreas(filters.areas);
            }

            // A11y: set aria-controls on the button if missing
            $('[data-role="open-map"]').attr({'aria-controls':'bl-map-modal', 'aria-haspopup':'dialog'});

            // read/apply view mode
            this.view = this.readStoredView();
            this.applyViewButtons(ui);
            this.applyViewToContainer(ui);

            ui.$btnGrid.on('click', () => {
                if (this.view === 'grid') return;
                this.view = 'grid';
                this.storeView(this.view);
                this.applyViewButtons(ui);
                this.applyViewToContainer(ui);
                this.applyViewToCards(ui.$grid);
                // list/grid in url params
                // this.updateUrl(this.buildSearchParams($scope), { push: false });
            });

            ui.$btnList.on('click', () => {
                if (this.view === 'list') return;
                this.view = 'list';
                this.storeView(this.view);
                this.applyViewButtons(ui);
                this.applyViewToContainer(ui);
                this.applyViewToCards(ui.$grid);
                // list/grid in url params
                // this.updateUrl(this.buildSearchParams($scope), { push: false });
            });

            // ----- APPLY RULES -----
            // 1) Rerun search only when needed
            const rerun = async ({ push = false } = {}) => {
                this.page = 1;
                await this.runSearch(ui, $scope, false, { push });
            };

            // Bedrooms — currently triggers rerun (keep), using replaceState
            $scope.on('click', '[data-role="bedrooms-btn"]', async (e) => {
                const $btn = $(e.currentTarget);
                const active = $btn.hasClass('data-active');
                $btn.closest('[data-role="bedrooms-buttons"]')
                    .find('[data-role="bedrooms-btn"]')
                    .removeClass('bg-accent text-accent-foreground data-active');
                if (!active) $btn.addClass('bg-accent text-accent-foreground data-active');
                this.updateClearAllButtons($scope);
                await rerun({ push: false });
            });

            $('body').on('click', '[data-role="open-filter-popup"]', (e) => {
                const $btn = $(e.currentTarget);
                const $filterPopup = $('#mobile-popup-filter');
                const $overlayPopup = $('#mobile-filter-overlay');
                const active = $btn.hasClass('data-active');
                if (!active) {
                    $btn.addClass('data-active');
                    $filterPopup.addClass('data-active');
                    $overlayPopup.addClass('data-active');
                }
            });

            $('body').on('click', '#mobile-filter-overlay, [data-role="close-popup"]', (e) => {
                const $btn = $('[data-role="open-filter-popup"]');
                const $filterPopup = $('#mobile-popup-filter');
                const $overlayPopup = $('#mobile-filter-overlay');

                $btn.removeClass('data-active');
                $filterPopup.removeClass('data-active');
                $overlayPopup.removeClass('data-active');
            });

            // features — instant (replace)
            $scope.on('change', 'input[data-role="features"]', () => {
                this.updateClearAllButtons($scope);
                rerun({ push: false });
            });

            $scope.on('change', '[data-role="unsure-checkout"]', () => {
                this.toggleUnsureCheckoutUI($scope);
                this.updateClearAllButtons($scope);
            });

            // Stay Type — instant (replace)
            $scope.on('change', 'input[data-role="staytype"]', async (e) => {
                if (e.currentTarget.checked) {
                    $(e.currentTarget).closest('[data-role="staytype-list"]')
                        .find('input[data-role="staytype"]').not(e.currentTarget).prop('checked', false);
                }
                this.updateClearAllButtons($scope);
                await rerun({ push: false });
            });

            // Search — only here we pushState
            $scope.find('[data-role="search"]').off('click').on('click', () => rerun({ push: true }));

            // Clear all — logically a new history step (push)
            $(document)
                .off('click.clearAll')
                .on('click.clearAll', '[data-role="clear-all"]', async (e) => {
                    e.preventDefault();

                    const $scope = this.$scopeRef || $(this.sidebarMountSelector).find('[data-role="filters-sidebar"]');

                    // reset everything within the sidebar scope
                    $scope.find('[data-role="bedrooms-btn"]').removeClass('bg-accent text-accent-foreground data-active');
                    $scope.find('input[type="checkbox"]').prop('checked', false);
                    $scope.find('select[data-role="location"]').val('').trigger('change');
                    $scope.find('select[data-role="guests"]').val('').trigger('change');
                    $scope.find('[data-role="checkin"]').val('').trigger('change');
                    $scope.find('[data-role="checkout"]').val('').trigger('change');

                    this.toggleUnsureCheckoutUI($scope);

                    this.extraFeatureIds = [];

                    // update visibility of both buttons (popup + desktop)
                    this.updateClearAllButtons($scope);

                    // run search with pushState
                    await rerun({ push: true });
                });

            // initial search
            await this.runSearch(ui, $scope, false);

        } catch (e) {
            console.error(e);
        }
    }

    // Unified search + render (first and load-more)
    async runSearch(ui, $scope, append = false, urlOpts = { push: false }) {
        if (this.loading) return;
        this.loading = true;

        const params = this.buildSearchParams($scope);

        // 1) URL: keep guests=display
        this.updateUrl(params, urlOpts);

        // 2) API: send maxGuests only (strip guests)
        const apiParams = { ...(params || {}) };
        delete apiParams.guests;
        delete apiParams.unsureCheckout;
        delete apiParams._isUnsureCheckout;
        delete apiParams._hasManualDateTo;

        const { data, total } = await this.getApartments(apiParams);

        // store overall total from backend
        this.total = Number.isFinite(total) ? total : (append ? this.total : (data?.length || 0));

        // location for the header
        const $opt = $scope.find('select[data-role="location"] option:selected');
        const locText = $opt.val() ? ($opt.text() || 'Basel').replace(/\s*\(\d+\)\s*$/,'') : 'Basel';

        // render list
        this.renderUnits(ui.$grid, data, append);

        // currently shown (for the "Load more" button)
        const shown = ui.$grid.children().length;

        // header always shows the OVERALL count from pagination.total
        this.updateHeader(ui.$title, this.total, locText);

        // handler for loading more
        const handleLoadMore = async () => {
            this.page += 1;
            // update URL with new page (replace)
            const p = this.buildSearchParams($scope);
            this.updateUrl(p, { push: false });

            const apiP = { ...(p || {}) };
            delete apiP.guests;
            delete apiP.unsureCheckout;
            delete apiP._isUnsureCheckout;
            delete apiP._hasManualDateTo;

            const more = await this.getApartments(apiP);
            this.renderUnits(ui.$grid, more.data, true);

            const newShown = ui.$grid.children().length;
            // do not change total (backend defines it)
            this.updateHeader(ui.$title, (Number.isFinite(more.total) ? more.total : this.total), locText);
            this.updateLoadMore(ui.$loadWrap, ui.$loadBtn, newShown, (Number.isFinite(more.total) ? more.total : this.total), handleLoadMore);
        };

        // update "Load more" button state
        this.updateLoadMore(ui.$loadWrap, ui.$loadBtn, shown, this.total, handleLoadMore);

        this.loading = false;
    }
}

/* ------------------------ INIT ------------------------ */
const overviewApartmentApi = new OverviewApartmentApi();
$(document).ready(() => overviewApartmentApi.boot());
